/**
 * The model's surface classes and the release policy, ported from production's Mighty BIM widget.
 *
 * THE BAKE DECLARES ITS OWN CLASSES: every tileset carries a `classes.json` beside its `tileset.json`, generated from
 * the same tables that produce the pixels. The policy (`bim-layers.json`) is the second file: whether a surface is
 * fit to be seen changes after publish, so it cannot live in a file that can never be edited. Two files, two
 * lifetimes, one join.
 */

/**
 * A source class as the rest of the app consumes it. `row` is the CLASS CODE, resolved from the tile's property
 * table by the engine, not the raw `_FEATURE_ID_0`. `group` is the baker's split: 'design' is the engineered
 * surfaces carried at full resolution through every LOD, 'ground' is everything the terrain is made of.
 */
export interface ClassDef {
  row: number;
  label: string;
  colour: string;
  group?: 'design' | 'ground';
  triangles?: number;
}

/**
 * How far along a surface is, in the only sense the app can act on: whether this deployment puts it in front of a
 * reader. Deliberately coarse; the register carries the engineering detail.
 */
export type Status = 'draft' | 'review' | 'released';

export const ALL_STATUS: readonly Status[] = ['draft', 'review', 'released'];
export const isStatus = (v: unknown): v is Status => v === 'draft' || v === 'review' || v === 'released';

/**
 * A class after the policy join, which is the only shape any consumer ever sees. `status` is NOT optional: it is
 * resolved once, at the join, and never again, so no reader has to remember that absent means draft.
 */
export interface ShownClass extends ClassDef {
  status: Status;
}

/** What the join found, so the panel can say it out loud rather than silently dropping rows. */
export interface PolicyReport {
  /** 'missing' covers a 404, a malformed file and a lapsed session: three causes, one consequence (nothing is
   *  being withheld). */
  src: 'file' | 'missing';
  render: Status[];
  /** Named in the policy, absent from the bake. Surfaced, never ignored: drift that nobody sees never gets fixed. */
  orphans: { code: number; status: Status }[];
  /** In the bake, not in the render list. A count only: a diagnostic, not UI. */
  withheld: number;
}

export interface Joined {
  classes: ShownClass[];
  /** Class codes the render list excluded. The engine has to be told about these separately. */
  withheld: number[];
  report: PolicyReport;
  /** Everything the bake declares, withheld included: some questions (which classes are 'design') are about the
   *  BAKE, not about what is on screen. */
  bake: ClassDef[];
  src: 'manifest' | 'fallback';
}

export interface Policy {
  render: Status[];
  classes: Record<number, Status>;
}

/**
 * What we fall back to when a tileset carries no `classes.json` (the older tilesets that predate it). Do not add
 * to it; add to the baker and rebake. Two entries changed meaning on 2026-08-04: 3 is the EARTHWORKS either side of
 * a track and 8 is the road surface itself.
 */
export const FALLBACK_CLASSES: readonly ClassDef[] = [
  { row: 0, label: 'LiDAR 10 m', colour: '#808078', group: 'ground' },
  { row: 1, label: 'LiDAR 2 m', colour: '#9e9987', group: 'ground' },
  { row: 2, label: 'Blend band', colour: '#ada180', group: 'ground' },
  { row: 3, label: 'Track earthworks', colour: '#ff6b1a', group: 'design' },
  { row: 4, label: 'Tower pad', colour: '#ffd400', group: 'design' },
  { row: 5, label: 'Global fill', colour: '#6b7075', group: 'ground' },
  { row: 6, label: 'Stringing pad', colour: '#2be86b', group: 'design' },
  { row: 7, label: 'Energy hub', colour: '#8c293b', group: 'design' },
  { row: 8, label: 'Track road surface', colour: '#8a3324', group: 'design' },
];

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A manifest into the shape the app speaks. Anything malformed returns null and the caller keeps the fallback: a
 * half-parsed manifest is worse than none (a class with no colour renders an invisible swatch, one with no code
 * cannot be toggled, and both look like the model is broken rather than the file).
 */
export function parseManifest(doc: unknown): ClassDef[] | null {
  const list = isRecord(doc) && Array.isArray(doc.classes) ? (doc.classes as unknown[]) : null;
  if (!list || !list.length) return null;
  const out: ClassDef[] = [];
  for (const c of list) {
    const r = isRecord(c) ? c : {};
    const row = Number(r.code);
    const label = typeof r.name === 'string' ? r.name : '';
    const colour = typeof r.colour === 'string' ? r.colour : '';
    if (!Number.isFinite(row) || !label || !/^#[0-9a-f]{6}$/i.test(colour)) return null;
    const triangles = Number(r.triangles);
    out.push({
      row,
      label,
      colour,
      group: r.group === 'design' ? 'design' : 'ground',
      ...(r.triangles != null && Number.isFinite(triangles) ? { triangles } : {}),
    });
  }
  return out;
}

/**
 * The release policy. THIS IS PRESENTATION POLICY, NOT A SECURITY BOUNDARY: every tile stays fetchable whatever this
 * file says; anything that must not leave the building has to be left out of the bake itself.
 *
 * All-or-nothing parsing, with the OPPOSITE consequence to the manifest: a malformed policy opens the gate
 * (everything renders), so it must be visible in the panel instead. Half a policy would hold back what it managed
 * to parse and quietly release the rest.
 */
export function parsePolicy(doc: unknown): Policy | null {
  if (!isRecord(doc)) return null;
  const raw = Array.isArray(doc.render) ? (doc.render as unknown[]) : null;
  const render = raw ? raw.filter(isStatus) : null;
  if (!raw || !render || !render.length || render.length !== raw.length) return null;
  const src = doc.classes;
  if (!isRecord(src)) return null;
  const classes: Record<number, Status> = {};
  for (const k of Object.keys(src)) {
    const code = Number(k);
    const entry = src[k];
    const st = isRecord(entry) ? entry.status : undefined;
    if (!Number.isFinite(code) || !isStatus(st)) return null;
    classes[code] = st;
  }
  return { render, classes };
}

/**
 * A project that is not this one, shown in the model beside it (the GIS's Other Projects group): which tower family,
 * conductor products and surface classes are its own. Declared in the release policy's optional `groups`, so a new
 * one is a policy edit, never a build (production, 21 Oct 2026).
 */
export interface ProjectGroup {
  id: string;
  title: string;
  /** The schedule's `tower_family` these structures carry; they get their own Towers row. */
  towerFamily?: string;
  /** The row name for those structures. */
  towers?: string;
  /** Overlay manifest kinds (conductor products) that belong to the group. */
  layers: string[];
  /** Surface class codes that belong to the group. */
  classes: number[];
}

/**
 * Lenient where parsePolicy is strict, and the asymmetry is deliberate: a group only moves rows under a heading, it
 * never decides what renders, so a bad entry is dropped on its own and the rest of the policy stands.
 */
export function parseGroups(doc: unknown): ProjectGroup[] {
  const src = isRecord(doc) && Array.isArray(doc.groups) ? (doc.groups as unknown[]) : [];
  const out: ProjectGroup[] = [];
  for (const g of src) {
    if (!isRecord(g) || typeof g.id !== 'string' || !g.id || typeof g.title !== 'string' || !g.title) continue;
    const layers = Array.isArray(g.layers) ? (g.layers as unknown[]) : [];
    const classes = Array.isArray(g.classes) ? (g.classes as unknown[]) : [];
    out.push({
      id: g.id,
      title: g.title,
      ...(typeof g.towerFamily === 'string' && g.towerFamily ? { towerFamily: g.towerFamily } : {}),
      ...(typeof g.towers === 'string' && g.towers ? { towers: g.towers } : {}),
      layers: layers.filter((k): k is string => typeof k === 'string' && !!k),
      classes: classes.map(Number).filter((n) => Number.isFinite(n)),
    });
  }
  return out;
}

/**
 * Join the bake against the policy. Three rules, each of which fails SILENTLY if got wrong:
 *
 * - A class in the bake with no line in the policy is a DRAFT. Fail closed: a new surface nobody has looked at must
 *   never reach a reader because somebody forgot a line.
 * - A class in the policy that the bake does not contain is SURFACED (orphans): the two halves have drifted apart.
 * - A missing or unparseable policy renders EVERYTHING and says so: a 404 on a policy file must not blank the model.
 */
export function joinPolicy(bake: readonly ClassDef[], pol: Policy | null, src: 'manifest' | 'fallback'): Joined {
  const render: Status[] = pol ? pol.render : [...ALL_STATUS];
  const classes: ShownClass[] = [];
  const withheld: number[] = [];
  for (const c of bake) {
    const status: Status = (pol && pol.classes[c.row]) || 'draft';
    if (render.includes(status)) classes.push({ ...c, status });
    else withheld.push(c.row);
  }
  const inBake = new Set(bake.map((c) => c.row));
  const orphans = pol
    ? Object.keys(pol.classes)
        .map(Number)
        .filter((n) => !inBake.has(n))
        .sort((a, b) => a - b)
        .map((n) => ({ code: n, status: pol.classes[n] as Status }))
    : [];
  return {
    classes,
    withheld,
    bake: [...bake],
    src,
    report: { src: pol ? 'file' : 'missing', render, orphans, withheld: withheld.length },
  };
}

/** Class codes the imagery drape must leave alone: every engineered surface the BAKE declares (withheld included,
 *  because whether a photograph may cover a surface is a fact about what the surface IS). */
export function designRows(join: Joined): number[] {
  return join.bake.filter((c) => c.group === 'design').map((c) => c.row);
}
